import { useState, useEffect } from 'react';

export default function Modal({
    open,
    mode,
    data,
    estudiantes = [],
    premios = [],
    empleados = [],
    personas = [],
    inline,
    onClose,
    onSubmit,
}) {
    const [form, setForm] = useState({
        idEstudiante: '',
        idPremio: '',
        costo: '',
        fechaCompra: '',
        activo: true,
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (mode === 'edit' && data) {
            setForm({
                idEstudiante: data.idEstudiante || '',
                idPremio: data.idPremio || '',
                costo: data.costo || '',
                fechaCompra: data.fechaCompra ? data.fechaCompra.split('T')[0] : '',
                activo: data.activo !== undefined ? data.activo : true,
            });
        } else {
            setForm({
                idEstudiante: '',
                idPremio: '',
                costo: '',
                fechaCompra: new Date().toISOString().split('T')[0],
                activo: true,
            });
        }
        setErrors({});
    }, [mode, data, open]);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    const validateField = (field, value) => {
        switch (field) {
            case 'idEstudiante':
                return value ? '' : 'Selecciona un estudiante';
            case 'idPremio':
                return value ? '' : 'Selecciona un premio';
            case 'costo':
                if (!value) return 'El costo es obligatorio';
                if (Number(value) < 0) return 'El costo no puede ser negativo';
                return '';
            case 'fechaCompra':
                return value ? '' : 'La fecha de compra es obligatoria';
            default:
                return '';
        }
    };

    const validate = () => {
        const newErrors = {};
        Object.keys(form).forEach((key) => {
            if (key !== 'activo') {
                const error = validateField(key, form[key]);
                if (error) newErrors[key] = error;
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;
        onSubmit(form, mode);
    };

    // Helper para obtener nombre del estudiante
    const getEstudianteNombre = (idEstudiante) => {
        const estudiante = estudiantes.find(e => Number(e.idEstudiante) === Number(idEstudiante));
        if (!estudiante) return '';

        const idEmpleadoValue = estudiante.idEmpleado || estudiante.IdEmpleado;
        const empleado = empleados.find(emp => Number(emp.idEmpleado) === Number(idEmpleadoValue));
        if (!empleado) return `Estudiante #${idEstudiante}`;

        const idPersonaValue = empleado.idPersona || empleado.IdPersona;
        const persona = personas.find(p => Number(p.idPersona) === Number(idPersonaValue));
        if (!persona) return `Estudiante #${idEstudiante}`;

        const nombre = persona.nombre || persona.Nombre || '';
        const ap = persona.apellidoPaterno || persona.ApellidoPaterno || '';
        const am = persona.apellidoMaterno || persona.ApellidoMaterno || '';

        return `${nombre} ${ap} ${am}`.trim() || `Estudiante #${idEstudiante}`;
    };

    // Crear opciones de estudiantes
    const estudiantesOptions = estudiantes.map(est => ({
        value: est.idEstudiante,
        label: getEstudianteNombre(est.idEstudiante) || `Estudiante #${est.idEstudiante}`,
    })).sort((a, b) => a.label.localeCompare(b.label));

    // Crear opciones de premios
    const premiosOptions = premios
        .filter(p => p.activo) // Solo premios activos
        .map(premio => ({
            value: premio.idPremio,
            label: premio.nombre || `Premio #${premio.idPremio}`,
            costo: premio.costo,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    // Actualizar costo cuando se selecciona un premio
    const handlePremioChange = (idPremio) => {
        handleChange('idPremio', idPremio);
        const premio = premios.find(p => p.idPremio === Number(idPremio));
        if (premio && premio.costo) {
            handleChange('costo', premio.costo);
        }
    };

    if (!open) return null;

    const errorStyle = {
        color: '#b91c1c',
        fontSize: '.8rem',
        display: 'block',
        marginTop: '4px',
    };

    const Header = (
        <div
            className="roles-topbar"
            style={{
                marginBottom: '0.6rem',
                paddingRight: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flex: '0 0 auto',
            }}>
            <h3 className="roles-title" style={{ marginRight: 0 }}>
                {mode === 'new' ? 'Nueva Compra de Premio' : 'Editar Compra de Premio'}
            </h3>
            <button className="roles-btn" onClick={onClose} aria-label="Cerrar">
                ✕
            </button>
        </div>
    );

    const Body = (
        <div
            style={{
                paddingLeft: '15%',
                paddingRight: 16,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.8rem',
                overflowY: 'auto',
                flex: '1 1 auto',
                minHeight: 0,
            }}>

            {/* Estudiante */}
            <div>
                <label className="roles-title" style={{ fontSize: '.95rem' }}>Estudiante:</label>
                <br /><br />
                <select
                    className={`infoField${errors.idEstudiante ? ' infoField--error' : ''}`}
                    value={form.idEstudiante}
                    onChange={(e) => handleChange('idEstudiante', e.target.value)}
                    disabled={mode === 'edit'}
                >
                    <option value="">Selecciona un estudiante...</option>
                    {estudiantesOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
                {errors.idEstudiante && <small style={errorStyle}>{errors.idEstudiante}</small>}
            </div>

            {/* Premio */}
            <div>
                <label className="roles-title" style={{ fontSize: '.95rem' }}>Premio:</label>
                <br /><br />
                <select
                    className={`infoField${errors.idPremio ? ' infoField--error' : ''}`}
                    value={form.idPremio}
                    onChange={(e) => handlePremioChange(e.target.value)}
                    disabled={mode === 'edit'}
                >
                    <option value="">Selecciona un premio...</option>
                    {premiosOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label} ({opt.costo} monedas)
                        </option>
                    ))}
                </select>
                {errors.idPremio && <small style={errorStyle}>{errors.idPremio}</small>}
            </div>

            {/* Costo */}
            <div>
                <label className="roles-title" style={{ fontSize: '.95rem' }}>Costo (monedas):</label>
                <br /><br />
                <input
                    type="number"
                    className={`infoField${errors.costo ? ' infoField--error' : ''}`}
                    value={form.costo}
                    onChange={(e) => handleChange('costo', e.target.value)}
                    min="0"
                    step="1"
                />
                {errors.costo && <small style={errorStyle}>{errors.costo}</small>}
            </div>

            {/* Fecha de Compra */}
            <div>
                <label className="roles-title" style={{ fontSize: '.95rem' }}>Fecha de Compra:</label>
                <br /><br />
                <input
                    type="date"
                    className={`infoField${errors.fechaCompra ? ' infoField--error' : ''}`}
                    onKeyDown={(e) => e.preventDefault()}
                    value={form.fechaCompra}
                    onChange={(e) => handleChange('fechaCompra', e.target.value)}
                />
                {errors.fechaCompra && <small style={errorStyle}>{errors.fechaCompra}</small>}
            </div>
        </div>
    );

    const Footer = (
        <div
            style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '.6rem',
                marginTop: '.8rem',
                paddingRight: 16,
                flex: '0 0 auto',
            }}
        >
            <button className="roles-btn" onClick={onClose}>Cancelar</button>
            <button
                className="btn-action"
                onClick={handleSubmit}
                disabled={!form.idEstudiante || !form.idPremio || !form.costo || !form.fechaCompra}
                style={{
                    opacity: (!form.idEstudiante || !form.idPremio || !form.costo || !form.fechaCompra) ? 0.5 : 1,
                    cursor: (!form.idEstudiante || !form.idPremio || !form.costo || !form.fechaCompra) ? 'not-allowed' : 'pointer',
                }}
            >
                {mode === 'new' ? 'Guardar' : 'Actualizar'}
            </button>
        </div>
    );

    if (inline) {
        return (
            <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {Header}
                {Body}
                {Footer}
            </div>
        );
    }

    return (
        <div
            className="roles-confirm-overlay"
            role="dialog"
            aria-modal="true"
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(15,23,42,.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                zIndex: 1000,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div
                className="roles-confirm-card"
                style={{
                    width: 'min(900px, 96vw)',
                    maxHeight: '85vh',
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 10px 30px rgba(0,0,0,.25)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {Header}
                {Body}
                {Footer}
            </div>
        </div>
    );
}
